/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';

export type DayRange = [number, number];

export const CHIP_SIZE = 32;

export const dayOptions = [
  { label: 'Today', value: 0 },
  { label: '+1', value: 1 },
  { label: '+2', value: 2 },
  { label: '+3', value: 3 },
  { label: '+4', value: 4 },
  { label: '+5', value: 5 },
  { label: '+6', value: 6 },
  { label: '+7', value: 7 },
];

export const MIN_DAY = dayOptions[0].value;
export const MAX_DAY = dayOptions[dayOptions.length - 1].value;

export const clampDay = (day: number): number =>
  Math.min(MAX_DAY, Math.max(MIN_DAY, day));

export const normaliseRange = (anchor: number, focus: number): DayRange => [
  clampDay(Math.min(anchor, focus)),
  clampDay(Math.max(anchor, focus)),
];

export const isSameRange = (range: DayRange, other: DayRange): boolean =>
  range[0] === other[0] && range[1] === other[1];

export const isDayInRange = (day: number, range: DayRange): boolean =>
  day >= Math.min(...range) && day <= Math.max(...range);

export const resolveDayFromClientX = (
  clientX: number,
  containerLeft: number,
  chipSize = CHIP_SIZE,
): number => clampDay(Math.floor((clientX - containerLeft) / chipSize));

export const getNextFocus = (focus: number, key: string): number | null => {
  switch (key) {
    case 'ArrowLeft':
      return clampDay(focus - 1);
    case 'ArrowRight':
      return clampDay(focus + 1);
    case 'Home':
      return MIN_DAY;
    case 'End':
      return MAX_DAY;
    default:
      return null;
  }
};

export const getDateForDay = (day: number, now: Date): Date =>
  dateUtils.add(dateUtils.startOfDayUTC(now), { days: day });
